import React from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {usePath} from 'hooks';
import {ANALYTIC_EVENTS, Analytics, useIVStore} from '@chekin/shared';
import {useGetGuestId} from '../../../hooks/useGetGuestId';
import {PATHS} from 'routes/constants';
import {ButtonProps} from '@chekinapp/ui';
import ModalSheet from '../ModalSheet';
import StandardModalSheet from '../StandardModalSheet';
import {useModalControls} from '@chekin/guestapp-ui-kit';

export type SkipIVModalRef = {
  open: (link?: string) => void;
};

const SkipIVModal = React.forwardRef<SkipIVModalRef, unknown>((_, ref) => {
  const {t} = useTranslation();
  const {
    isOpen: isOpenSkipVerificationModal,
    closeModal: closeSkipModal,
    openModal: openSkipModal,
  } = useModalControls();
  const guestId = useGetGuestId();
  const navigate = useNavigate();
  const {resetAllIVState} = useIVStore();

  const {getMainPath} = usePath();

  const onSkippedModal = React.useCallback(() => {
    const pathIdOrEmptyString = guestId || '';
    resetAllIVState();
    Analytics.track(ANALYTIC_EVENTS.ivSkipping);
    navigate(`${getMainPath(PATHS.addPersonalDataForm)}/${pathIdOrEmptyString}`);
  }, [resetAllIVState, navigate, getMainPath, guestId]);

  const modalSheetButtons: ButtonProps[] = React.useMemo(() => {
    const primaryButton: ButtonProps = {
      children: 'skip_by_now',
      onClick: onSkippedModal,
    };
    const secondaryButton: ButtonProps = {
      variant: 'link',
      children: 'continue_verification',
      onClick: closeSkipModal,
    };
    return [primaryButton, secondaryButton];
  }, [closeSkipModal, onSkippedModal]);

  React.useImperativeHandle(
    ref,
    () => ({
      open: openSkipModal,
    }),
    [openSkipModal],
  );

  return (
    <ModalSheet
      isOpenModalSheet={isOpenSkipVerificationModal}
      handleCloseModal={closeSkipModal}
      title={t('skip_verification_question')}
      withCloseButton
      closeButtonType="click"
    >
      <StandardModalSheet buttons={modalSheetButtons} text={t('skip_iv_process_text')} />
    </ModalSheet>
  );
});

export {SkipIVModal};
